// The `buoys` structure symbol set: card 19 of mockup round 054, "IALA
// BUOYS" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// Each kind is one buoy of the chart, in elevation on a short waterline: the
// body in the holder's ink and one topmark per kind. The Trench Throne is a
// pillar with a crown, the Relay Spire a spar with two cones up, the Forge
// Vent a cone with one cone up, the Kelp Terrace a barrel with the special
// cross, the Deep Battery a can with two cones down, the Drydock Shoal a cone
// on a can with the two balls of an isolated danger, the Archive Vault a
// sphere with one ball, the Marshal Court a can with two cones base to base,
// and the Coral Ward a sphere with two cones point to point.
//
// The contested tell: the buoy lists 15 degrees and the light on its head
// burns in the attacker's ink. The card rocks the buoy about that list and
// blinks the light; the atlas bakes the still mark (R1104), so the list
// stands at 15 degrees, the rock is gone and the light stands at the middle
// of the blink.
//
// **The transform is baked.** The card places the buoy with `translate`,
// `rotate` and `scale`; `MarkGraphics` carries no transform, so `Stance`
// below holds the three figures and `at()` puts every point through them.
// The waterline is drawn before the transform, so it stays level under a
// listing buoy, which is what the card shows.
//
// Every offset, weight, radius and alpha below is the figure card 19 draws.

import type { MarkGraphics } from '../marks';
import {
  BLINK, roundRectPoints, stroke, WATER, setOf, type StructureDraw, type StructureInk,
  type StructureKind, type StructureSet,
} from './shared';

/** Where the waterline stands, and how far the buoy lists when attacked. */
const WATERLINE = 13;
const LIST_DEG = 15;
/** The capital rides a little higher out of the water. */
const THRONE_SCALE = 1.05;

/** The place, the list and the size one buoy is drawn at. */
interface Stance {
  scale: number;
  cos: number;
  sin: number;
}

function stanceOf(kind: StructureKind, contested: boolean): Stance {
  const a = contested ? (LIST_DEG * Math.PI) / 180 : 0;
  return {
    scale: kind === 'throne' ? THRONE_SCALE : 1,
    cos: Math.cos(a),
    sin: Math.sin(a),
  };
}

/** One point of the buoy, put through the scale, the list and the waterline. */
function at(p: Stance, x: number, y: number): [number, number] {
  const sx = x * p.scale, sy = y * p.scale;
  return [sx * p.cos - sy * p.sin, sx * p.sin + sy * p.cos + WATERLINE];
}

/** A point list of the buoy, put through the stance. */
function path(p: Stance, pts: number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < pts.length; i += 2) {
    const [x, y] = at(p, pts[i], pts[i + 1]);
    out.push(x, y);
  }
  return out;
}

/** A stroked segment of the buoy. The list carries the line width as well. */
function bline(
  g: MarkGraphics, p: Stance, x0: number, y0: number, x1: number, y1: number,
  color: number, alpha: number, width: number,
): void {
  const [ax, ay] = at(p, x0, y0), [bx, by] = at(p, x1, y1);
  g.moveTo(ax, ay).lineTo(bx, by);
  stroke(g, color, alpha, width * p.scale);
}

/** A filled disc of the buoy. */
function bdisc(
  g: MarkGraphics, p: Stance, x: number, y: number, r: number,
  color: number, alpha: number,
): void {
  const [cx, cy] = at(p, x, y);
  g.circle(cx, cy, r * p.scale).fill({ color, alpha });
}

/** The body and its outline, which every buoy draws the same way. */
function shape(
  g: MarkGraphics, p: Stance, pts: number[], color: number,
  alpha: number, fillAlpha: number, width = 1.4,
): void {
  const poly = path(p, pts);
  g.poly(poly).fill({ color, alpha: fillAlpha });
  g.poly(poly);
  stroke(g, color, alpha, width * p.scale);
}

/** The ink one buoy is drawn in, and the alphas of its body. */
interface Paint {
  color: number;
  alpha: number;
  fill: number;
}

/** The seven bodies of the chart. `conecan` is the cone on a can of an isolated danger. */
type Body = 'can' | 'cone' | 'sphere' | 'pillar' | 'spar' | 'barrel' | 'conecan';

/**
 * One of the seven bodies of the chart. It answers the y of its head, which
 * is where the topmark of the kind stands.
 */
function body(
  g: MarkGraphics, p: Stance, k: Body, q: Paint,
): number {
  const { color, alpha: a, fill: fa } = q;
  switch (k) {
    case 'can':
      shape(g, p, [-6, 0, 6, 0, 6, -12, -6, -12], color, a, fa);
      bline(g, p, -6, -4, 6, -4, color, a * 0.8, 1);
      bline(g, p, -6, -8, 6, -8, color, a * 0.8, 1);
      return -12;
    case 'cone':
      shape(g, p, [-7, 0, 7, 0, 0, -13], color, a, fa);
      bline(g, p, -4.8, -4, 4.8, -4, color, a * 0.8, 1);
      return -13;
    case 'sphere': {
      const [cx, cy] = at(p, 0, -6.5), r = 6.5 * p.scale;
      g.circle(cx, cy, r).fill({ color, alpha: fa });
      g.circle(cx, cy, r).stroke({ width: 1.4 * p.scale, color, alpha: a });
      bline(g, p, -6.3, -5, 6.3, -5, color, a * 0.8, 1);
      return -13;
    }
    case 'pillar':
      shape(g, p, [-7, 0, 7, 0, 3, -17, -3, -17], color, a, fa);
      bline(g, p, -6, -3, 3, -17, color, a * 0.7, 1);
      bline(g, p, 6, -3, -3, -17, color, a * 0.7, 1);
      bline(g, p, -5, -8, 5, -8, color, a * 0.7, 1);
      return -17;
    case 'spar':
      shape(g, p, [-2, 0, 2, 0, 1.5, -18, -1.5, -18], color, a, fa);
      bline(g, p, -2, -6, 2, -6, color, a, 1);
      bline(g, p, -2, -12, 2, -12, color, a, 1);
      return -18;
    case 'barrel':
      // The two legs the barrel rides on, then the barrel and its bands.
      bline(g, p, -5, 0, -5, -3, color, a, 1.2);
      bline(g, p, 5, 0, 5, -3, color, a, 1.2);
      shape(g, p, roundRectPoints(-9, -10, 18, 7, 3), color, a, fa);
      bline(g, p, -3, -10, -3, -3, color, a * 0.8, 1);
      bline(g, p, 3, -10, 3, -3, color, a * 0.8, 1);
      return -10;
    case 'conecan':
      shape(g, p, [-6, 0, 6, 0, 6, -8, -6, -8], color, a, fa);
      shape(g, p, [-6, -8, 6, -8, 0, -17], color, a, fa);
      return -17;
  }
}

/** One cone of a topmark, point up or point down, its base at `y`. */
function topCone(
  g: MarkGraphics, p: Stance, y: number, up: boolean, color: number, alpha: number,
): void {
  const b = up ? y : y - 5, t = up ? y - 5 : y;
  g.poly(path(p, [-3.5, b, 3.5, b, 0, t])).fill({ color, alpha });
}

/** The topmark of one kind. It answers the y of the head of the whole buoy. */
function topmark(
  g: MarkGraphics, p: Stance, kind: StructureKind, q: Paint,
): number {
  const { color, alpha: a } = q;
  let top: number;
  switch (kind) {
    case 'throne':
      // The crown of the capital, over a short staff.
      top = body(g, p, 'pillar', q) - 1;
      bline(g, p, 0, top + 1, 0, top - 3, color, a, 1.2);
      g.poly(path(p, [
        -6, top - 3, -6, top - 10, -3, top - 6,
        0, top - 11, 3, top - 6, 6, top - 10, 6, top - 3,
      ])).fill({ color, alpha: a });
      return top - 11;
    case 'spire':
      top = body(g, p, 'spar', q) - 1;
      topCone(g, p, top, true, color, a);
      topCone(g, p, top - 6, true, color, a);
      return top - 11;
    case 'forge':
      top = body(g, p, 'cone', q) - 1;
      topCone(g, p, top, true, color, a);
      return top - 5;
    case 'kelp':
      // The special cross of the chart, over a short staff.
      top = body(g, p, 'barrel', q) - 1;
      bline(g, p, 0, top + 1, 0, top - 2, color, a, 1.2);
      bline(g, p, -3.5, top - 8, 3.5, top - 2, color, a, 1.8);
      bline(g, p, 3.5, top - 8, -3.5, top - 2, color, a, 1.8);
      return top - 8;
    case 'battery':
      top = body(g, p, 'can', q) - 1;
      topCone(g, p, top, false, color, a);
      topCone(g, p, top - 6, false, color, a);
      return top - 11;
    case 'drydock':
      // The two balls of an isolated danger.
      top = body(g, p, 'conecan', q) - 1;
      bdisc(g, p, 0, top - 2.4, 2.4, color, a);
      bdisc(g, p, 0, top - 7.6, 2.4, color, a);
      return top - 10;
    case 'archive':
      top = body(g, p, 'sphere', q) - 1;
      bline(g, p, 0, top + 1, 0, top - 2, color, a, 1.2);
      bdisc(g, p, 0, top - 5, 3, color, a);
      return top - 8;
    case 'marshal':
      top = body(g, p, 'can', q) - 1;
      topCone(g, p, top, false, color, a);
      topCone(g, p, top - 5, true, color, a);
      return top - 10;
    default:
      top = body(g, p, 'sphere', q) - 1;
      topCone(g, p, top, true, color, a);
      topCone(g, p, top - 5, false, color, a);
      return top - 10;
  }
}

/** The short waterline under the buoy. It never lists. */
function waterline(g: MarkGraphics, color: number, alpha: number): void {
  const pts: number[] = [];
  for (let x = -17; x <= 17; x += 1) {
    pts.push(x, WATERLINE + Math.sin(x * 0.9) * 0.9);
  }
  g.poly(pts, false);
  stroke(g, color, alpha * 0.45, 1);
}

/**
 * The light on the head of a contested buoy, in the attacker's ink.
 *
 * The glow, the four rays and the lamp with its dark pupil. The card blinks
 * the glow and the rays; here they stand at the middle of the blink.
 */
function light(g: MarkGraphics, p: Stance, top: number, foe: number): void {
  const ly = top - 3.5;
  bdisc(g, p, 0, ly, 6, foe, 0.18 * BLINK);
  for (let k = 0; k < 4; k++) {
    const a = -Math.PI / 2 + (k - 1.5) * 0.7;
    bline(g, p, Math.cos(a) * 3.2, ly + Math.sin(a) * 3.2,
      Math.cos(a) * 6.5, ly + Math.sin(a) * 6.5,
      foe, 0.4 + 0.6 * BLINK, 1.2);
  }
  bdisc(g, p, 0, ly, 2.4, foe, 1);
  const [cx, cy] = at(p, 0, ly);
  g.circle(cx, cy, 2.4 * p.scale)
    .stroke({ width: 0.8 * p.scale, color: WATER, alpha: 0.9 });
}

/** One kind of the `buoys` set, still. */
function markOf(kind: StructureKind): StructureDraw {
  return (g: MarkGraphics, ink: StructureInk): void => {
    const contested = ink.foe !== 0;
    const q: Paint = {
      color: ink.own,
      alpha: ink.held ? 0.95 : 0.7,
      fill: ink.held ? 0.3 : 0.1,
    };
    const p = stanceOf(kind, contested);
    waterline(g, ink.own, q.alpha);
    const top = topmark(g, p, kind, q);
    if (contested) light(g, p, top, ink.foe);
  };
}

/** Card 19, all nine kinds. */
export const BUOYS_SET: StructureSet = setOf(markOf);
